import { useRoute } from "@react-navigation/native";
import React, { memo, useState, useCallback } from "react";
import { ActivityIndicator, StyleSheet, FlatList, View } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { ORDER_FLOW_EVENTS } from "@constants/analyticsEventsCT";
import { actions as customerActions } from "@reducers/customerReducer";
import CartBottomStrip from "@screens/Home/CartBottomStrip";
import analytics from "@services/analytics";
import { getCMSEntityById } from "@services/cmsV2-api";
import { isCLApp } from "../../config";
import useCMSEvents from "../../hooks/useCMSEvents";
import CategoryHeader from "./CategoryHeader";
import RenderCMSEntity from "./RenderCMSEntity";

const PlaceOrderStrip = ({
  navigation,
  selectedContact,
  cartDetails,
  cartItems,
  dispatch,
}) => {
  return selectedContact ? (
    <CartBottomStrip
      orderClick={() => {
        navigation?.push("ConfirmOrder");
        analytics.trackEvent(ORDER_FLOW_EVENTS.CHECKOUT_STARTED, {
          screen: "HOME",
          cart_value: cartDetails?.total,
          cart_quantity: Array.isArray(cartItems)
            ? cartItems.reduce((acc, curr) => (acc += curr.quantity), 0)
            : "",
        });
      }}
      onCrossClick={() => {
        dispatch(customerActions.setSelectedContact(false));
        analytics.trackEvent(ORDER_FLOW_EVENTS.CX_CANCELLED);
      }}
      total={cartDetails.total}
      item={cartItems.length}
      name={selectedContact?.user_name}
    />
  ) : null;
};

const FETCH_LIMIT = 15;
const SubPageHeader = memo(
  ({
    items = [],
    categoryTitle = "",
    navigation,
    showBackBtnHeader = false,
    isFloatingHeader = false,
    sharingTemplate,
    sharingLink,
  }) => {
    return (
      <View style={{ flex: 1, backgroundColor: "white" }}>
        {showBackBtnHeader && (
          <CategoryHeader
            title={categoryTitle}
            navigation={navigation}
            showSearch={false}
            showWhatsappLogo={false}
          />
        )}
      </View>
    );
  },
);

const rootScreens = ["Home", "PlaceOrderSuccess", "Gullak", "ConfirmOrder"];

const _keyExtractor = (item, index) => {
  return `key-${item?.cms_item_id ?? index}`;
};

const SubPage = ({
  isFloatingHeader = false,
  show_back_btn_header = false,
  navigation,
  list_items = [],
  cl_title = "",
  cx_title = "",
  // items = [],
  sharingTemplate,
  sharingLink,
  fromCart = false,
  cms_item_id: pageId,
}) => {
  const categoryTitle = isCLApp ? cl_title : cx_title;
  const dispatch = useDispatch();

  const route = useRoute();

  const { selectedContact, cartDetails, cartItems } = useSelector((state) => ({
    selectedContact: state.customer.selectedContact,
    cartDetails: state.cart.cartDetails,
    cartItems: state.cart.cartItems,
  }));

  const trackEvent = useCMSEvents(route.name);
  const isRoot = rootScreens.includes(route.name);
  const containsSKU =
    Array.isArray(list_items) &&
    list_items.some((item) => item?.type === "SKU_LIST");
  const [endReached, setEndReached] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cmsData, setCmsData] = useState(list_items || []);
  const _ListFooterComponent = useCallback(() => {
    if (endReached) {
      return null;
    }
    return (
      <View style={styles.listFooterComponent}>
        <ActivityIndicator color="red" animating={isLoading} />
        <View style={styles.height_18} />
      </View>
    );
  }, [isLoading, endReached]);

  const _renderItem = useCallback(
    ({ item }) => {
      const { cms_item_id } = item || {};
      return (
        <RenderCMSEntity
          id={cms_item_id}
          key={cms_item_id}
          trackEvent={trackEvent}
          isRoot={true}
          fromCart={fromCart}
          {...item}
        />
      );
    },
    [fromCart],
  );

  const onEnd = async () => {
    if (isLoading || endReached || !list_items || list_items?.length === 0) {
      return;
    }
    setIsLoading(true);
    const offset = cmsData.length;
    const limit = FETCH_LIMIT;
    try {
      const { data } = await getCMSEntityById({ id: pageId, limit, offset });
      if (data.list_items && data.list_items.length) {
        setCmsData((oldData) => [...oldData, ...data.list_items]);
      } else {
        setEndReached(true);
      }
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
    }
  };
  return (
    <>
      <FlatList
        data={cmsData}
        keyExtractor={_keyExtractor}
        ListHeaderComponent={() => (
          <SubPageHeader
            isFloatingHeader={isFloatingHeader}
            showBackBtnHeader={show_back_btn_header}
            categoryTitle={categoryTitle}
            navigation={navigation}
            sharingTemplate={sharingTemplate}
            sharingLink={sharingLink}
          />
        )}
        renderItem={_renderItem}
        ListFooterComponent={_ListFooterComponent}
        onEndReached={onEnd}
        onEndReachedThreshold={0.5}
      />

      {!isRoot && containsSKU ? (
        <PlaceOrderStrip
          {...{
            dispatch,
            navigation,
            selectedContact,
            cartDetails,
            cartItems,
          }}
        />
      ) : null}
    </>
  );
};

const styles = StyleSheet.create({
  listFooterComponent: {
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  height_18: {
    height: 18,
  },
});

export default SubPage;
